import { inject } from '@angular/core';
import { CanActivateFn, Router } from '@angular/router';
import { catchError, map, of, switchMap } from 'rxjs';
import { CompanyType } from '../../shared/enum/company-type.enum';
import { UserModel } from '../models/user.model';
import { AuthService } from '../services/auth.service';

const canAccessPrivateRoutes = (session: UserModel | null): boolean => {
  const companyTypes = session?.company?.types ?? [];

  if (!companyTypes.includes(CompanyType.CUSTOMER)) {
    return false;
  }

  // Allow only CUSTOMER and CUSTOMER+SUPPLIER combinations.
  return companyTypes.every(
    (type) => type === CompanyType.CUSTOMER || type === CompanyType.SUPPLIER
  );
};

export const authGuard: CanActivateFn = () => {
  const authService = inject(AuthService);
  const router = inject(Router);
  const toLogin = () => router.createUrlTree(['/login']);
  const toUnauthorized = () => router.createUrlTree(['/unauthorized']);

  const checkAccess = (session: UserModel | null) => {
    if (!session) return toLogin();
    return canAccessPrivateRoutes(session) ? true : toUnauthorized();
  };

  const current = authService.currentUser();

  // If company data is already present in the cached user, short-circuit
  if (current?.company) {
    return checkAccess(current);
  }

  // Company may be absent when currentUser was set from a login response that
  // doesn't include relations. Fetch the full profile from /auth/me first.
  return authService.restoreSession().pipe(
    switchMap((session) => {
      // restoreSession short-circuits when currentUser() is set — if company is
      // still missing after that, force a fresh /auth/me call.
      if (session && !session.company) {
        return authService.fetchCurrentUser();
      }
      return of(session);
    }),
    map(checkAccess),
    catchError(() => of(toLogin()))
  );
};
